import { computed, onScopeDispose, ref, shallowRef, watch } from "vue";
import { useFinderMapFocus } from "#ce/apps/SpFinderMap/composables/useFinderMapFocus";
import { useFinderMapScene } from "#ce/apps/SpFinderMap/composables/useFinderMapScene";
import { useFinderMapLayout } from "#ce/apps/SpFinderMap/composables/useFinderMapLayout";
import { mapLink } from "#ce/apps/SpFinderMap/lib/map-link";
import { markerRecords } from "#ce/apps/SpFinderMap/lib/map-records";

/**
 * Runs the small-screen map dialog for one selected result: its camera, the result's pin and info
 * window once the map settles, the external map link, and closing, which clears the map selection.
 * `dialog` and `canvas` are the overlay's template refs.
 */
export function useFinderMapMobileOverlay(props, { dialog, canvas }) {
  const open = ref(true);
  const map = shallowRef();
  const maps = shallowRef();
  const content = shallowRef();
  const error = shallowRef();
  const settled = ref(false);
  useFinderMapFocus(dialog);
  const infoTarget = document.createElement("div");
  const { offset } = useFinderMapLayout({ canvas, fallback: () => props.settings.stickyOffset });
  const layoutStyle = computed(() => ({
    top: `${offset.value}px`,
    height: `calc(100dvh - ${offset.value}px)`,
    position: "fixed",
    left: 0,
    right: 0,
    background: "var(--sp-comp-finder-map-background, var(--sp-sys-color-surface))",
  }));
  // Inline token bridges travel with the controls through the dialog's slot boundary.
  const controlStyle = computed(() => ({
    top: `${offset.value}px`,
    position: "fixed",
    zIndex: 1,
    width: "100%",
    padding: "var(--sp-comp-finder-map-mobile-control-inset, var(--sp-ref-scale-20)) var(--sp-ref-scale-20) 0",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: "var(--sp-ref-scale-10)",
  }));
  const options = computed(() => {
    const position = markerRecords([props.result])[0]?.position;
    return {
      center: position ?? props.settings.defaultCenter ?? { lat: 0, lng: 0 },
      zoom: position ? 13 : (props.settings.defaultZoom ?? 13),
      maxZoom: 18,
      disableDefaultUI: true,
      zoomControl: false,
      gestureHandling: "cooperative",
    };
  });
  const externalUrl = computed(() => {
    const search = mapLink(props.result.map_url);
    if (search) {
      return search;
    }
    const position = markerRecords([props.result])[0]?.position;
    if (!position) {
      return undefined;
    }
    const url = new URL("https://www.google.com/maps/search/");
    url.searchParams.set("api", "1");
    url.searchParams.set("query", `${position.lat},${position.lng}`);
    return url.toString();
  });
  function renderInfo(value) {
    content.value = value;
    return infoTarget;
  }
  const { scene } = useFinderMapScene({
    map,
    maps,
    records: () => [props.result],
    icons: () => props.settings.icons ?? {},
    renderInfo,
  });
  function showProvider() {
    const current = scene.value;
    if (!current || !settled.value) {
      return;
    }
    const marker = current.markerFor(props.result.resultGUID);
    if (marker) {
      current.open(props.result, marker, { shouldFocus: false });
      map.value.panBy?.(0, -200);
    }
  }
  function markSettled() {
    settled.value = true;
  }
  function ready({ detail: [value] }) {
    maps.value = value.maps;
    map.value = value.map;
    value.maps.event.addListenerOnce(value.map, "idle", markSettled);
  }
  function failed({ detail: [value] }) {
    error.value = value;
  }
  function disposed() {
    map.value = undefined;
    maps.value = undefined;
    settled.value = false;
  }
  function close() {
    if (!open.value) {
      return;
    }
    open.value = false;
    props.commands.clearMapSelection();
  }
  function updatedOpen({ detail: [value] }) {
    if (!value) {
      close();
    }
  }
  watch([scene, () => props.result, settled], showProvider, { flush: "post" });
  onScopeDispose(() => {
    infoTarget.remove();
  });
  return {
    open,
    error,
    content,
    infoTarget,
    layoutStyle,
    controlStyle,
    options,
    externalUrl,
    ready,
    failed,
    disposed,
    close,
    updatedOpen,
  };
}
